import { useEffect, useState } from "react";
import { Alert, Button, ControlLabel, FormControl, FormGroup, Modal } from "react-bootstrap";
import { ISaveHardToMatchDonorReviewRequest } from "@nkr/models/hard-to-match/hard-to-match-donor-review";
import { IHardToMatchPair } from "@nkr/models/hard-to-match/hard-to-match-pair";
import useHardToMatchReviewStatuses from "@nkr/store/hard-to-match/hooks/use-hard-to-match-review-statuses";

interface IProps {
    pair: IHardToMatchPair | null;
    onSave: (request: ISaveHardToMatchDonorReviewRequest) => Promise<string>;
    onClose: () => void;
}

const EditReviewModal = ({ pair, onSave, onClose }: IProps) => {
    const { statuses, error: statusesError } = useHardToMatchReviewStatuses();

    const [status, setStatus] = useState<string>("");
    const [note, setNote] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        setStatus(pair?.reviewStatus != null ? pair.reviewStatus.toString() : "");
        setNote(pair?.reviewNote || "");
        setError("");
    }, [pair]);

    const isRetiredStatus =
        pair?.reviewStatus != null && !statuses.some((x) => x.id == pair.reviewStatus);

    const save = async () => {
        setSaving(true);

        const result = await onSave({
            recipientId: pair.recipientId,
            donorId: pair.donorId,
            status: status == "" ? null : parseInt(status),
            note,
        });

        setSaving(false);

        if (result == "") {
            onClose();
        } else {
            setError(result);
        }
    };

    return (
        <Modal backdrop="static" show={pair != null} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    Hard-to-Match Status: {pair?.recipientAlias} / {pair?.donorAlias}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {(error || statusesError) && (
                    <Alert bsStyle="danger">{error || statusesError}</Alert>
                )}

                <FormGroup>
                    <ControlLabel>Status</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={status}
                        onChange={(e) => setStatus((e as any).currentTarget.value)}
                    >
                        <option value="">- None -</option>
                        {isRetiredStatus && (
                            <option value={pair.reviewStatus}>
                                {pair.reviewStatusDescription}
                                {pair.isHidden ? " [Hides Row]" : ""}
                            </option>
                        )}
                        {statuses.map((x) => (
                            <option key={x.id} value={x.id}>
                                {x.description}
                                {x.hidesPair ? " [Hides Row]" : ""}
                            </option>
                        ))}
                    </FormControl>
                </FormGroup>

                <FormGroup>
                    <ControlLabel>Notes</ControlLabel>
                    <FormControl
                        componentClass="textarea"
                        rows={5}
                        value={note}
                        onChange={(e) => setNote((e as any).currentTarget.value)}
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="primary" disabled={saving} onClick={save}>
                    Save
                </Button>
                <Button disabled={saving} onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditReviewModal;
